import { ActionIcon, Button, Group, Modal, Skeleton, Space, Stack, Table, Textarea } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { IconCheck, IconPlus, IconTrash } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { createApiKeys, deleteApiKeys, getApiKeys, updateApiKeys, account } from '@mygpt/app';

const { ContentWrap } = account;
import { CopyButton } from '@/components/CopyButton';
import ContainerHeader from '@/components/Headers/ContainerHeader';

import classes from './index.module.css';

interface apiKeysListParam {
  apiKey: string;
  createdAt: string;
  isLoading?: boolean;
  description?: string;
}

interface requestApiKeys {
  api_key: string;
  created_at: string;
  description?: string;
}

interface ApiKey {
  apiKey: string;
  description: string;
}

const ApiKeysPage = () => {
  const { t } = useTranslation();

  const [apiKeysList, setApiKeysList] = useState<apiKeysListParam[]>([]);
  const [initLoading, setInitLoading] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [newKey, setNewKey] = useState<ApiKey | null>(null);
  const [delKey, setDelKey] = useState('');

  const initData = async () => {
    setInitLoading(true);
    const res = await getApiKeys();
    if (res.data) {
      const list = res.data.map((item: requestApiKeys) => {
        return {
          apiKey: item.api_key,
          description: item.description,
          createdAt: item.created_at,
        };
      });
      setApiKeysList(list);
    }
    setInitLoading(false);
  };

  const create = async () => {
    setCreateLoading(true);
    setUpdateSuccess(false);
    setApiKeysList([
      ...apiKeysList,
      {
        apiKey: '',
        createdAt: '',
        isLoading: true,
      },
    ]);

    createApiKeys()
      .then((res) => {
        setNewKey({
          apiKey: res.data.api_key,
          description: res.data.description,
        });
      })
      .catch((error) => {
        // 达上限等情况后端返回 400，需向用户提示，避免骨架行闪退、静默失败 + Sentry 噪声
        const detail = error?.response?.data?.detail;
        showNotification({
          color: 'red',
          message: detail || t('errors.common'),
        });
      })
      .finally(() => {
        setTimeout(initData, 350);
        setCreateLoading(false);
      });
  };

  const delApiKey = () => {
    setDeleteLoading(true);
    deleteApiKeys({ apiKey: delKey })
      .then(() => {
        setDelKey('');
        initData();
      })
      .finally(() => {
        setDeleteLoading(false);
      });
  };

  const updateApiKey = () => {
    if (!newKey) return;
    setUpdateLoading(true);
    updateApiKeys(newKey).finally(() => {
      initData();
      setUpdateLoading(false);
      setUpdateSuccess(true);
    });
  };

  const cancelCreateApiKey = () => {
    if (!newKey) return;
    // 取消时删除刚创建的 API Key，然后关闭 Modal
    setCancelLoading(true);
    deleteApiKeys({ apiKey: newKey.apiKey })
      .then(() => {
        setNewKey(null);
        initData();
      })
      .finally(() => {
        setCancelLoading(false);
      });
  };

  useEffect(() => {
    initData();
  }, []);

  return (
    <>
      <ContainerHeader title={t('api_key.title')} description={t('api_key.desc')} />
      <ContentWrap className="p-5">
        <Table verticalSpacing="sm">
          <Table.Thead>
            <Table.Tr>
              <Table.Th className={classes.borderBottomColor}>
                <span className="w-auto sm:w-[350px] text-gray-400 font-light">{t('api_key.table.th_key')}</span>
              </Table.Th>
              <Table.Th className={`${classes.borderBottomColor} w-2/4`}>
                <span className="text-gray-400 font-light">{t('api_key.table.th_description')}</span>
              </Table.Th>
              <Table.Th className={classes.borderBottomColor}>
                <span className="text-gray-400 font-light">{t('api_key.table.th_created')}</span>
              </Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {apiKeysList.map((item, index) => (
              <Table.Tr key={index} className="hover:bg-gray-50 transition-all">
                {item.isLoading ? (
                  <>
                    <Table.Td>
                      <Skeleton height={8} radius="xl" />
                    </Table.Td>
                    <Table.Td>
                      <Skeleton height={8} radius="xl" />
                    </Table.Td>
                    <Table.Td>
                      <Skeleton height={8} radius="xl" />
                    </Table.Td>
                  </>
                ) : (
                  <>
                    <Table.Td>
                      <span
                        className="text-black font-light"
                        // onClick={() => {
                        //   setNewKey({
                        //     apiKey: item.apiKey,
                        //     description: item.description ?? '',
                        //   });
                        // }}
                      >
                        {item.apiKey}
                      </span>
                    </Table.Td>
                    <Table.Td>
                      <span className="text-black font-light">{item.description}</span>
                    </Table.Td>
                    <Table.Td>
                      <Group justify="space-between">
                        <span className="text-black font-light">
                          {dayjs(item.createdAt).format('YYYY-MM-DD HH:mm:ss')}
                        </span>
                        <ActionIcon
                          color="red"
                          size={'sm'}
                          onClick={() => {
                            setDelKey(item.apiKey);
                          }}
                        >
                          <IconTrash size="1rem" />
                        </ActionIcon>
                      </Group>
                    </Table.Td>
                  </>
                )}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
        <Space h="xl" />

        <Button
          variant="gradient"
          gradient={{ from: '#6d28d9', to: '#be81ff' }}
          leftSection={<IconPlus />}
          loading={createLoading || initLoading}
          onClick={() => {
            create();
          }}
        >
          {t('api_key.create_new_key')}
        </Button>
      </ContentWrap>

      <Modal centered opened={!!newKey} onClose={() => {}} size="lg" withCloseButton={false}>
        <Stack>
          <h3 className="text-[16px] sm:text-[22px] font-bold">{t('api_key.modal.create.title')}</h3>
          <div className="text-[14px] sm:text-[16px]">{t('api_key.modal.create.desc')}</div>

          <Group justify="space-between" align="center" className={`${classes.input} mt-5`}>
            <p className={`${classes.inputKey} truncate text-[12px] sm:text-[14px]`}>{newKey?.apiKey || ''}</p>
            <CopyButton apiKey={newKey?.apiKey} />
          </Group>

          <Textarea
            value={newKey?.description}
            onChange={(e) => {
              if (!newKey) return;
              setNewKey({
                ...newKey,
                description: e.target.value,
              });
            }}
            label={t('api_key.table.th_description')}
          />

          <Group justify="flex-end" mt={30}>
            <Button variant="outline" loading={cancelLoading} onClick={cancelCreateApiKey}>
              {t('alert_cancel')}
            </Button>
            {updateSuccess ? (
              <Button
                className="bg-green-500 hover:bg-green-500"
                leftSection={<IconCheck />}
                onClick={() => {
                  setNewKey(null);
                }}
              >
                {t('save_success')}
              </Button>
            ) : (
              <Button loading={updateLoading} onClick={() => updateApiKey()}>
                {t('save')}
              </Button>
            )}
          </Group>
        </Stack>
      </Modal>

      <Modal
        centered
        opened={!!delKey}
        onClose={() => {
          setDelKey('');
        }}
        size="lg"
        withCloseButton={false}
      >
        <Stack>
          <h3 className="text-[16px] sm:text-[22px] font-bold">{t('api_key.modal.revoke.title')}</h3>
          <div className="text-[14px] sm:text-[16px]">{t('api_key.modal.revoke.desc')}</div>

          <Group justify="space-between" align="center" className={`${classes.input} mt-5`}>
            <p className={`${classes.inputKey} truncate text-[12px] sm:text-[14px]`}>{delKey}</p>
          </Group>

          <Group justify="flex-end" mt={30}>
            <Button
              onClick={() => {
                setDelKey('');
              }}
            >
              {t('alert_cancel')}
            </Button>
            <Button color="red" loading={deleteLoading} onClick={delApiKey}>
              {t('api_key.modal.revoke.submit')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
};

export default ApiKeysPage;
